/**
 * Escalas reutilizables del cuestionario.
 *
 * Están aquí y no inline en cada pregunta porque §9.6 obliga a que la ESCALA
 * de los reactivos del núcleo sea idéntica entre oleadas: un solo punto de
 * definición evita que una oleada futura altere una escala por accidente.
 */
import { PARTIES, COALITIONS } from '../catalog/parties.js';
export const YES_NO = [
    { code: 'si', label: 'Sí' },
    { code: 'no', label: 'No' },
];
export const YES_NO_NC = [
    { code: 'si', label: 'Sí' },
    { code: 'no', label: 'No' },
    { code: 'nc', label: 'No contestó', nonSubstantive: true, spontaneousOnly: true },
];
/** C1 · rumbo del municipio. */
export const DIRECTION = [
    { code: 'buen_camino', label: 'Buen camino' },
    { code: 'mal_camino', label: 'Mal camino' },
    {
        code: 'ni_uno_ni_otro',
        label: 'Ni uno ni otro',
        spontaneousOnly: true,
        interviewerHint: 'No leer; registrar sólo si la persona lo dice por su cuenta.',
    },
    { code: 'ns_nc', label: 'NS / NC', nonSubstantive: true, spontaneousOnly: true },
];
/** C4 · calificación de servicios en la colonia. */
export const SERVICE_RATING = [
    { code: 'bueno', label: 'Bueno', value: 3 },
    { code: 'regular', label: 'Regular', value: 2 },
    { code: 'malo', label: 'Malo', value: 1 },
    { code: 'no_hay', label: 'No hay servicio' },
    { code: 'ns', label: 'NS', nonSubstantive: true },
];
/** G1–G3 · evaluación de gobiernos. */
export const GOV_RATING = [
    { code: 'muy_bueno', label: 'Muy bueno', value: 5 },
    { code: 'bueno', label: 'Bueno', value: 4 },
    { code: 'regular', label: 'Regular', value: 3 },
    { code: 'malo', label: 'Malo', value: 2 },
    { code: 'muy_malo', label: 'Muy malo', value: 1 },
    { code: 'ns', label: 'NS', nonSubstantive: true },
];
/** I1 · notoriedad asistida. El orden es de mayor a menor conocimiento. */
export const KNOWS_PERSONAJE = [
    { code: 'conoce_bien', label: 'Sí, lo/la conozco bien' },
    { code: 'ha_oido', label: 'Lo/la he oído mencionar' },
    { code: 'no_conoce', label: 'No lo/la conozco' },
];
/**
 * §10.1.2–3 — notoriedad bruta = conoce bien + ha oído;
 * notoriedad ponderada = conoce bien + 0.5 × ha oído (convención interna que
 * debe mantenerse fija entre oleadas).
 */
export const KNOWS_CODES_AWARE = ['conoce_bien', 'ha_oido'];
/**
 * Conocimiento efectivo, el que sostiene una asociación espontánea.
 *
 * `ha_oido` cuenta como notoriedad en §10.1.2 —ahí la pregunta es si el
 * nombre suena— pero no como base de I6, donde se pide qué se le viene a la
 * mente: quien sólo oyó el nombre no tiene nada que decir.
 */
export const KNOWS_PERSONAJE_WELL_CODE = 'conoce_bien';
export const NOTORIETY_WEIGHTS = {
    conoce_bien: 1,
    ha_oido: 0.5,
    no_conoce: 0,
};
/** I2 · opinión del personaje. El documento fija los valores 5..1. */
export const OPINION_PERSONAJE = [
    { code: 'muy_buena', label: 'Muy buena', value: 5 },
    { code: 'buena', label: 'Buena', value: 4 },
    { code: 'regular', label: 'Regular', value: 3 },
    { code: 'mala', label: 'Mala', value: 2 },
    { code: 'muy_mala', label: 'Muy mala', value: 1 },
    { code: 'sin_opinion', label: 'No tiene opinión', nonSubstantive: true },
    /**
     * Cierre automático: lo escribe el servidor para quien declaró en I1 que NO
     * conoce al personaje. El encuestador nunca ve esta opción ni esa fila.
     */
    { code: 'no_conoce', label: 'No lo/la conoce (cierre automático)', nonSubstantive: true, systemAssigned: true },
];
/** P4 · opinión de partidos y de la coalición. */
export const OPINION_PARTY = [
    { code: 'muy_buena', label: 'Muy buena', value: 5 },
    { code: 'buena', label: 'Buena', value: 4 },
    { code: 'regular', label: 'Regular', value: 3 },
    { code: 'mala', label: 'Mala', value: 2 },
    { code: 'muy_mala', label: 'Muy mala', value: 1 },
    { code: 'no_conoce', label: 'No lo conoce', nonSubstantive: true },
];
/** I3 · confianza para resolver los problemas de Tecate. */
export const TRUST = [
    { code: 'mucho', label: 'Mucho', value: 4 },
    { code: 'algo', label: 'Algo', value: 3 },
    { code: 'poco', label: 'Poco', value: 2 },
    { code: 'nada', label: 'Nada', value: 1 },
    { code: 'ns', label: 'NS', nonSubstantive: true },
    /**
     * Cierre automático: lo escribe el servidor para quien declaró en I1 que NO
     * conoce al personaje. El encuestador nunca ve esta opción ni esa fila.
     */
    { code: 'no_conoce', label: 'No lo/la conoce (cierre automático)', nonSubstantive: true, systemAssigned: true },
];
/** §10.1.5 — confianza efectiva = mucho + algo. */
export const TRUST_EFFECTIVE_CODES = ['mucho', 'algo'];
/** I4 · asociación personaje–partido. */
export const PARTY_ASSOCIATION = [
    ...PARTIES.map((p) => ({ code: p.id, label: p.label })),
    { code: 'varios', label: 'Con varios' },
    { code: 'ninguno', label: 'Con ninguno' },
    { code: 'ns', label: 'NS', nonSubstantive: true },
    /**
     * Cierre automático: lo escribe el servidor para quien declaró en I1 que NO
     * conoce al personaje. El encuestador nunca ve esta opción ni esa fila.
     */
    { code: 'no_conoce', label: 'No lo/la conoce (cierre automático)', nonSubstantive: true, systemAssigned: true },
];
/**
 * P5 · rechazo partidista, LISTA CERRADA.
 *
 * El documento la define como espontánea («Abierta → códigos», «no leer la
 * lista»). Por decisión del comando se convierte en selección múltiple con los
 * 9 partidos a la vista, hasta 2.
 *
 * NO mide lo mismo que la versión espontánea: con la lista delante el
 * entrevistado reconoce partidos que no habría nombrado solo, así que el
 * rechazo sube y se reparte distinto. El dato es más completo y comparable
 * entre entrevistas, pero deja de ser «rechazo top-of-mind» y no es comparable
 * con el §10.2.4 tal como está redactado. Queda asentado aquí para que quien
 * analice no mezcle las dos cosas.
 */
export const PARTY_REJECTION = [
    ...PARTIES.map((p) => ({ code: p.id, label: p.label })),
    { code: 'ninguno', label: 'Ninguno / a todos les votaría', exclusive: true },
    { code: 'ns_nc', label: 'NS / NC', nonSubstantive: true, exclusive: true, spontaneousOnly: true },
];
/* -------------------------------------------------------------------------- */
/*                     Cierre automático de la batería                        */
/* -------------------------------------------------------------------------- */
/**
 * Quien contesta en I1 que NO conoce a un personaje no recibe I2–I5 sobre él,
 * y el servidor cierra esas cuatro filas con un código propio.
 *
 * Antes esas celdas quedaban simplemente VACÍAS, y una celda vacía no dice si
 * el entrevistado no conocía al personaje, si la entrevista se interrumpió o
 * si hubo un fallo de captura. Ahora lo dice.
 *
 * El código es `no_conoce` en las cinco preguntas —el mismo que en I1— y NO se
 * reusa `sin_opinion` ni `ns`: ésos son respuestas legítimas de alguien que SÍ
 * conoce al personaje, y mezclarlos rompería el denominador de §10.1.4
 * (favorabilidad neta sobre base conocedores) y la distinción entre base
 * conocedores y base total de §2.2.
 */
export const KNOWS_PERSONAJE_NEGATIVE_CODE = 'no_conoce';
export const BATTERY_SOURCE_QUESTION = 'I1';
export const BATTERY_AUTO_CLOSED_QUESTIONS = ['I2', 'I3', 'I4', 'I5'];
/** I5 · disposición a votar por el personaje. */
export const VOTE_DISPOSITION = [
    { code: 'seguro', label: 'Seguro votaría por él/ella' },
    { code: 'podria', label: 'Podría votar por él/ella' },
    { code: 'nunca', label: 'Nunca votaría por él/ella' },
    { code: 'ns', label: 'NS', nonSubstantive: true },
    /**
     * Cierre automático: lo escribe el servidor para quien declaró en I1 que NO
     * conoce al personaje. El encuestador nunca ve esta opción ni esa fila.
     */
    { code: 'no_conoce', label: 'No lo/la conoce (cierre automático)', nonSubstantive: true, systemAssigned: true },
];
/**
 * §10.1.7 — piso = "seguro"; potencial = "seguro + podría";
 * rechazo duro = "nunca". Todos sobre BASE TOTAL, no base conocedores.
 */
export const DISPOSITION_FLOOR_CODES = ['seguro'];
export const DISPOSITION_POTENTIAL_CODES = ['seguro', 'podria'];
export const DISPOSITION_HARD_REJECTION_CODES = ['nunca'];
/** X1 · consideración de la candidatura con etiqueta partidista. */
export const CONSIDERATION = [
    { code: 'muy_probable', label: 'Muy probable', value: 4 },
    { code: 'algo_probable', label: 'Algo probable', value: 3 },
    { code: 'poco_probable', label: 'Poco probable', value: 2 },
    { code: 'nada_probable', label: 'Nada probable', value: 1 },
    { code: 'ns_nc', label: 'NS / NC', nonSubstantive: true },
];
/** §10.2.5 — consideración = muy + algo probable. */
export const CONSIDERATION_POSITIVE_CODES = ['muy_probable', 'algo_probable'];
/** P2 · intensidad de la identidad partidista. */
export const PARTY_IDENTIFICATION = [
    { code: 'muy', label: 'Muy identificado(a)' },
    { code: 'algo', label: 'Algo identificado(a)' },
    { code: 'poco', label: 'Poco identificado(a)' },
];
/** §10.2.2 — identidad dura = "muy"; identidad blanda = algo/poco. */
export const HARD_ID_CODES = ['muy'];
export const SOFT_ID_CODES = ['algo', 'poco'];
/** E2 · interés en política. */
export const POLITICAL_INTEREST = [
    { code: 'mucho', label: 'Mucho', value: 4 },
    { code: 'algo', label: 'Algo', value: 3 },
    { code: 'poco', label: 'Poco', value: 2 },
    { code: 'nada', label: 'Nada', value: 1 },
];
/** P1 · simpatía partidista. */
export const PARTY_SYMPATHY = [
    ...PARTIES.map((p) => ({ code: p.id, label: p.label })),
    { code: 'otro', label: 'Otro', requiresSpecify: true },
    { code: 'ninguno', label: 'Ninguno' },
    { code: 'ns_nc', label: 'NS / NC', nonSubstantive: true },
];
/** P3 · inclinación de independientes. */
export const PARTY_LEAN = [
    ...PARTIES.map((p) => ({ code: p.id, label: p.label })),
    { code: 'ninguno', label: 'Ninguno' },
];
/**
 * P8 · preferencia partidista para la gubernatura 2027.
 * Lista de 9 partidos + Juntos Haremos Historia + Alianza PAN-PRI-PES BC.
 */
export const GOVERNOR_PREFERENCE = [
    ...PARTIES.map((p) => ({ code: p.id, label: p.label })),
    { code: 'JHH', label: 'Juntos Haremos Historia' },
    { code: 'PAN_PRI_PES', label: 'Alianza PAN-PRI-PES BC' },
    { code: 'otro', label: 'Otro', requiresSpecify: true },
    { code: 'ninguno', label: 'Ninguno' },
    { code: 'ns', label: 'NS', nonSubstantive: true },
];
/**
 * P6 · recuerdo de voto municipal 2024.
 * Los 7 candidatos que compitieron en Tecate (corrección #6 de la v3).
 */
export const VOTE_RECALL_2024_MUNICIPAL = [
    { code: 'roman_cota', label: 'Román Cota (MORENA–PVEM–FxM)' },
    { code: 'dario_benitez', label: 'Darío Benítez (PES BC)' },
    { code: 'edna_huerta', label: 'Edna Huerta (PAN)' },
    { code: 'karla_barajas', label: 'Karla Barajas (PT)' },
    { code: 'maria_dolores_ortiz', label: 'María Dolores Ortiz (PRI)' },
    { code: 'anel_perreta', label: 'Anel Perreta (MC)' },
    { code: 'maria_refugio_felix', label: 'María del Refugio Félix (PRD)' },
    { code: 'no_voto', label: 'No votó' },
    { code: 'no_recuerda', label: 'No recuerda', nonSubstantive: true },
    { code: 'nc', label: 'NC', nonSubstantive: true },
];
/** P7 · recuerdo de voto presidencial 2024. */
export const VOTE_RECALL_2024_PRESIDENTIAL = [
    { code: 'sheinbaum', label: 'Claudia Sheinbaum' },
    { code: 'galvez', label: 'Xóchitl Gálvez' },
    { code: 'maynez', label: 'Jorge Álvarez Máynez' },
    { code: 'otro', label: 'Otro', requiresSpecify: true },
    { code: 'no_voto', label: 'No votó' },
    { code: 'no_recuerda', label: 'No recuerda', nonSubstantive: true },
    { code: 'nc', label: 'NC', nonSubstantive: true },
];
/** Códigos de P6 que implican haber votado (insumo del modelo de §9.3). */
export const P6_VOTED_CODES = [
    'roman_cota',
    'dario_benitez',
    'edna_huerta',
    'karla_barajas',
    'maria_dolores_ortiz',
    'anel_perreta',
    'maria_refugio_felix',
];
/* -------------------------------------------------------------------------- */
/*                        Sociodemográficos (Bloque 11)                       */
/* -------------------------------------------------------------------------- */
/** S1 · sexo. Admite "Otro" por el posible registro no binario de §3.1. */
export const SEX = [
    { code: 'mujer', label: 'Mujer' },
    { code: 'hombre', label: 'Hombre' },
    { code: 'otro', label: 'Otro' },
];
/** S3 · escolaridad. 8 categorías alineadas con la cuota (corrección #9 v2). */
export const EDUCATION = [
    { code: 'sin_escolaridad', label: 'Sin escolaridad' },
    { code: 'primaria_incompleta', label: 'Primaria incompleta' },
    { code: 'primaria_completa', label: 'Primaria completa' },
    { code: 'secundaria_incompleta', label: 'Secundaria incompleta' },
    { code: 'secundaria_completa', label: 'Secundaria completa' },
    { code: 'preparatoria_tecnica', label: 'Preparatoria o técnica' },
    { code: 'licenciatura', label: 'Licenciatura' },
    { code: 'posgrado', label: 'Posgrado' },
];
/** S4 · ocupación principal. */
export const OCCUPATION = [
    { code: 'empleado_formal', label: 'Empleado(a) formal' },
    { code: 'trabajo_informal', label: 'Trabajo informal' },
    { code: 'negocio_propio', label: 'Negocio propio' },
    { code: 'labores_hogar', label: 'Labores del hogar' },
    { code: 'jubilado', label: 'Jubilado(a)' },
    { code: 'estudiante', label: 'Estudiante' },
    { code: 'desempleado', label: 'Desempleado(a)' },
    { code: 'otro', label: 'Otro', requiresSpecify: true },
];
/** S6 · años viviendo en Tecate. */
export const YEARS_IN_TECATE = [
    { code: 'menos_2', label: 'Menos de 2 años' },
    { code: '2_5', label: '2 a 5 años' },
    { code: '6_10', label: '6 a 10 años' },
    { code: '11_20', label: '11 a 20 años' },
    { code: 'mas_20', label: 'Más de 20 años' },
    { code: 'toda_la_vida', label: 'Toda la vida' },
];
/** S7 · lugar de nacimiento. */
export const BIRTHPLACE = [
    { code: 'tecate', label: 'Tecate' },
    { code: 'otro_municipio_bc', label: 'Otro municipio de BC' },
    { code: 'otro_estado', label: 'Otro estado', requiresSpecify: true },
    { code: 'otro_pais', label: 'Otro país', requiresSpecify: true },
];
/** S10 · ingreso mensual del hogar. Va al final por su alta no respuesta. */
export const HOUSEHOLD_INCOME = [
    { code: 'menos_8k', label: 'Menos de $8,000' },
    { code: '8_15k', label: '$8,000 a $15,000' },
    { code: '15_25k', label: '$15,000 a $25,000' },
    { code: '25_40k', label: '$25,000 a $40,000' },
    { code: 'mas_40k', label: 'Más de $40,000' },
    { code: 'nc', label: 'NC', nonSubstantive: true },
];
/* -------------------------------------------------------------------------- */
/*                   Filas de baterías con catálogo propio                    */
/* -------------------------------------------------------------------------- */
/** C4 · servicios de la colonia, en orden rotado. */
export const COLONIA_SERVICES = [
    { code: 'agua_potable', label: 'Agua potable', rotates: true },
    { code: 'seguridad', label: 'Seguridad / vigilancia', rotates: true },
    { code: 'calles', label: 'Calles y pavimentación', rotates: true },
    { code: 'alumbrado', label: 'Alumbrado público', rotates: true },
    { code: 'basura', label: 'Recolección de basura', rotates: true },
    { code: 'transporte', label: 'Transporte público', rotates: true },
];
/** V1–V6 · temas de vinculación personaje–tema, en orden rotado. */
export const VINCULATION_THEMES = [
    { code: 'V1', label: 'Seguridad pública', rotates: true },
    { code: 'V3', label: 'Empleo y economía local', rotates: true },
    { code: 'V4', label: 'Honestidad / combate a la corrupción', rotates: true },
    { code: 'V5', label: 'Cercanía con la gente y las colonias', rotates: true },
    { code: 'V6', label: 'Obras y desarrollo de Tecate', rotates: true },
];
/** P4 · filas: los 9 partidos más la coalición. */
export const PARTY_OPINION_ROWS = [
    ...PARTIES.map((p) => ({ code: p.id, label: p.label, rotates: true })),
    {
        code: 'JHH',
        label: COALITIONS[0].spokenLabel,
        rotates: false,
    },
];
//# sourceMappingURL=scales.js.map